import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type RoiStoreState = {
    isDirty: boolean,
    // True when a loaded ROI was created before PAR fees switched from monthly to
    // annual entry. Legacy ROIs keep their original monthly semantics (×12) and the
    // UI surfaces a banner prompting the user to recreate them. New ROIs are stamped
    // with pricingModel="annual" on save, so the absence of that marker = legacy.
    isLegacyRoi: boolean,
    activeStep: number,
    completedSteps: { [k: number]: boolean },
    savedBy?: {
        name: string;
        email: string;
        savedAt: string;
    };
    client: {
        name: string;
        annualNetPatientServiceRevenue?: number;
        currentParSolutions?: string[];
        // new | par360Addon | mixed | scaleExpansion. Older saves only have
        // isPar360AddOn; resolveCustomerSituation() maps that to par360Addon.
        customerSituation?: string;
        isPar360AddOn?: boolean;
    };
    sameDay: {
        isSameDayOnly: boolean;
        numberOfHospitals: number;
        spend: { [key: string]: any };
        investment: { [key: string]: any };
    };
    spendDetailsRows: {};
    clientCosts: {};
    laborCost: {};
    callDownCost: {};
    chargeCapture: {},
    investmentSummary: {};
    totals: {};
    errors: {};
};

// Initial state for Redux store:
const initialState: RoiStoreState = {
    isDirty: false,
    isLegacyRoi: false,
    activeStep: 0,
    completedSteps: {},
    client: {
        name: "",
        annualNetPatientServiceRevenue: 0,
        currentParSolutions: [],
        customerSituation: "new",
        isPar360AddOn: false,
    },
    sameDay: {
        isSameDayOnly: false,
        numberOfHospitals: 0,
        spend: {},
        investment: {},
    },
    spendDetailsRows: { row1: {} },
    clientCosts: {},
    laborCost: {},
    callDownCost: {},
    chargeCapture: {},
    investmentSummary: {},
    totals: {
        monthlyCallDowns: 0,
        annualSpendDetails: 0,
        scaleCount: 0,
        totalCallDowns: 0,
        totalHours: 0,
        savingsTable: [],
        savingsTotals: [],
        investmentTable: [],
        annualRoiTotals: [],
    },
    errors: {},
};

// Create Redux state slice
const roiSlice = createSlice({
    name: "roi",
    initialState, // Define initial state
    reducers: {
        // Define reducers
        initalState: initialState,
        setClient: (state, action) => {            
            state.client = {
                ...state.client,
                ...action.payload,
                currentParSolutions: action.payload.currentParSolutions ?? state.client.currentParSolutions ?? [],
                customerSituation: action.payload.customerSituation ?? state.client.customerSituation ?? "new",
                isPar360AddOn: action.payload.customerSituation !== undefined
                    ? action.payload.customerSituation === "par360Addon"
                    : (action.payload.isPar360AddOn ?? state.client.isPar360AddOn ?? false),
            };
            state.isDirty = true;
        },
        setSpendDetailsRows: (state, action) => {
            if (action.payload.name.length > 0) {
                if ((action.payload.value > 0)
                    || (action.payload.value.length > 0)
                    || state.spendDetailsRows["row" + action.payload.rowId][action.payload.name] !== action.payload.value) {
                    state.isDirty = true;
                }
            }
            
            state.spendDetailsRows["row" + action.payload.rowId] = {
                ...(state.spendDetailsRows["row" + action.payload.rowId] ?? {}),
                [action.payload.name]: action.payload.value,
            };
        },
        setClientCosts: (state, action) => {
            if (action.payload.value > 0 && state.clientCosts[action.payload.name] !== action.payload.value) {
                state.isDirty = true;
            }
            state.clientCosts[action.payload.name] = action.payload.value;
        },
        setLaborCost: (state, action) => {            
            if (action.payload.name.length > 0) {
                if ((action.payload.value > 0)                   
                    || (state.laborCost[action.payload.name] && state.laborCost[action.payload.name][action.payload.subValue] !== action.payload.value)) {
                    state.isDirty = true;
                }
            }

            state.laborCost[action.payload.name] = {
                ...state.laborCost[action.payload.name],
                [action.payload.subValue]: action.payload.value,
            };
        },
        setCallDownCost: (state, action) => {                        
            if ((action.payload.value > 0 && !state.callDownCost[action.payload.name])
                || state.callDownCost[action.payload.name][action.payload.subValue] !== action.payload.value) {
                state.isDirty = true;
            }
            state.callDownCost[action.payload.name] = {
                ...state.callDownCost[action.payload.name],
                [action.payload.subValue]: action.payload.value,
            };
        },
        setChargeCapture: (state, action) => {            
            if (action.payload.value > 0 && state.chargeCapture[action.payload.name][action.payload.subValue] !== action.payload.value) {
                state.isDirty = true;
            }
            state.chargeCapture[action.payload.name] = {
                ...state.chargeCapture[action.payload.name],
                [action.payload.subValue]: action.payload.value,
            };
        },
        resetChargeCapture: (state, action) => {           
            state.chargeCapture = initialState.chargeCapture;
        },
        setTotals: (state, action) => { 
            state.totals[action.payload.name] = action.payload.value;
        },

        setInvestmentSummary: (state, action) => {
            if (action.payload.value > 0 && state.investmentSummary[action.payload.name] !== action.payload.value) {
                state.isDirty = true;
            }
            state.investmentSummary[action.payload.name] = action.payload.value;            
        },

        removeSpendDetailsRow: (state, action) => {            
            state.isDirty = true;
            const key = `row${action.payload.rowId}`;
            const { [key]: _, ...rest } = state.spendDetailsRows;
            state.spendDetailsRows = rest;
        },
        setErrors: (state, action) => {
            state.errors[action.payload.name] = action.payload.value;
        },
        setActiveStep: (state, action) => {
            state.activeStep = action.payload;
        },
        setCompletedSteps: (state, action) => {
            state.completedSteps = action.payload;
        },
        setSameDayMode: (state, action: PayloadAction<{ isSameDayOnly: boolean; numberOfHospitals?: number }>) => {
            state.sameDay.isSameDayOnly = !!action.payload.isSameDayOnly;
            if (action.payload.numberOfHospitals !== undefined) {
                state.sameDay.numberOfHospitals = Number(action.payload.numberOfHospitals) || 0;
            }
            state.isDirty = true;
        },
        setSameDaySpend: (state, action: PayloadAction<{ name: string; value: any }>) => {
            if (state.sameDay.spend[action.payload.name] !== action.payload.value) {
                state.isDirty = true;
            }
            state.sameDay.spend[action.payload.name] = action.payload.value;
        },
        setSameDayInvestment: (state, action: PayloadAction<{ name: string; value: any }>) => {
            if (state.sameDay.investment[action.payload.name] !== action.payload.value) {
                state.isDirty = true;
            }
            state.sameDay.investment[action.payload.name] = action.payload.value;
        },
        resetStore: (state, action) => {
            state.client = initialState.client;
            state.sameDay = { ...initialState.sameDay, spend: {}, investment: {} };
            state.spendDetailsRows = initialState.spendDetailsRows;
            state.clientCosts = initialState.clientCosts;
            state.laborCost = initialState.laborCost;
            state.callDownCost = initialState.callDownCost;
            state.chargeCapture = initialState.chargeCapture;
            state.investmentSummary = initialState.investmentSummary;
            state.totals = initialState.totals;
            state.errors = initialState.errors;     
            state.isDirty = initialState.isDirty;
            state.isLegacyRoi = initialState.isLegacyRoi;
            state.activeStep = initialState.activeStep;
            state.completedSteps = initialState.completedSteps;
        },
        loadStore: (state, action) => {
            // Backward compatibility: pre-rebrand saved ROIs used "SameDay" as the
            // solution name. The product label is now "PAR SameDay" so normalize
            // any legacy values on load — otherwise the wizard wouldn't detect
            // SameDay and the SameDay-specific steps would not appear.
            const rawSolutions: string[] =
                action.payload?.client?.currentParSolutions ?? [];
            const normalizedSolutions = rawSolutions.map((s) => {
                const normalized = String(s ?? "").trim().toLowerCase();
                if (normalized === "sameday") return "PAR SameDay";
                if (
                    normalized === "esl" ||
                    normalized === "esls" ||
                    normalized === "electronic shelf label" ||
                    normalized === "electronic shelf labels" ||
                    normalized === "electronic shelf label (esl)" ||
                    normalized === "electronic shelf labels (esls)"
                ) {
                    return "Electronic Shelf Labels (ESLs)";
                }
                return s;
            });
            state.client = {
                ...initialState.client,
                ...(action.payload.client ?? {}),
                currentParSolutions: normalizedSolutions,
                customerSituation:
                    action.payload?.client?.customerSituation
                    || (action.payload?.client?.isPar360AddOn ? "par360Addon" : "new"),
                isPar360AddOn:
                    action.payload?.client?.customerSituation === "par360Addon"
                    || !!action.payload?.client?.isPar360AddOn,
            };
            // Backward compatibility: older saved ROIs have no `sameDay` block.
            const savedSameDay = action.payload.sameDay ?? {};
            state.sameDay = {
                isSameDayOnly: !!savedSameDay.isSameDayOnly,
                numberOfHospitals: Number(savedSameDay.numberOfHospitals ?? 0) || 0,
                spend: savedSameDay.spend ?? {},
                investment: savedSameDay.investment ?? {},
            };
            // Strip savedBy on load — it belongs to whoever originally saved this
            // ROI, not the person currently viewing/editing it.
            state.savedBy = undefined;
            state.spendDetailsRows = action.payload.spendDetailsRows;
            state.clientCosts = action.payload.clientCosts ?? {};
            state.laborCost = action.payload.laborCost;
            state.callDownCost = action.payload.callDownCost;
            state.chargeCapture = action.payload.chargeCapture;
            state.investmentSummary = action.payload.investmentSummary;
            state.totals = action.payload.totals;
            state.errors = action.payload.errors;
            // Backward compatibility: PAR subscription fees used to be entered as
            // monthly amounts and multiplied by 12 in the calculations. New ROIs are
            // now entered as annual amounts and stamped with pricingModel="annual".
            // Any saved ROI lacking that marker predates the change and must keep its
            // original monthly semantics so its figures still reconcile on reopen.
            const loadedInvestment = action.payload.investmentSummary ?? {};
            const loadedSameDayInvestment = savedSameDay.investment ?? {};
            const isAnnualPricing =
                loadedInvestment.pricingModel === "annual" ||
                loadedSameDayInvestment.pricingModel === "annual";
            state.isLegacyRoi = !isAnnualPricing;
        },      
    },
});

// Export actions generated by "createSlice()":
export const {
    initalState,
    setClient,
    setSpendDetailsRows,
    setClientCosts,
    setLaborCost,
    setCallDownCost,
    setChargeCapture,
    resetChargeCapture,
    setTotals,
    removeSpendDetailsRow,
    setInvestmentSummary,
    setErrors,
    setActiveStep,
    setCompletedSteps,
    setSameDayMode,
    setSameDaySpend,
    setSameDayInvestment,
    resetStore,
    loadStore,
} = roiSlice.actions;

// Export reducer generated by "createSlice()":
export default roiSlice.reducer;

